import React from 'react'
import { CloseIcon } from '@mote/assets/icons'
import { isNumber } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'

export interface CloseButtonProps
  extends Omit<HTMLXStyledProps<'button'>, 'w' | 'h'> {
  size?: number | 'sm' | 'md' | 'lg'
}

function normalizeIconSize(size: number): number {
  return size / 1.5
}

export const CloseButton = forwardRef<CloseButtonProps, 'button'>(
  (props, ref) => {
    return (
      <Root ref={ref} size={40} type='button' aria-label='Close' {...props}>
        <CloseIcon />
      </Root>
    )
  }
)

const Root = styled.buttonBox<CloseButtonProps>`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: black;
  border-radius: md;
  border: 0;
  padding: 0;
  appearance: none;
  user-select: none;
  background-color: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;

  ${({ size }) => {
    if (isNumber(size))
      return css`
        --close-button-size: ${size}px;
        --close-button-icon-size: ${normalizeIconSize(size as number)}px;
      `

    switch (size) {
      case 'sm':
        return css`
          --close-button-size: 24px;
          --close-button-icon-size: ${normalizeIconSize(24)}px;
        `
      case 'md':
        return css`
          --close-button-size: 32px;
          --close-button-icon-size: ${normalizeIconSize(32)}px;
        `
      case 'lg':
      default:
        return css`
          --close-button-size: 40px;
          --close-button-icon-size: ${normalizeIconSize(40)}px;
        `
    }
  }};

  width: var(--close-button-size);
  height: var(--close-button-size);

  svg {
    height: var(--close-button-icon-size);
    width: var(--close-button-icon-size);
  }

  &:hover,
  &:focus {
    background-color: ${th.color('grey.l4')};
  }

  &:focus {
    box-shadow: outline-primary;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
      box-shadow: none;
    `};
`

if (__DEV__) {
  CloseButton.displayName = 'CloseButton'
}
